/**
 * Model Scopes
 */

export enum WorkflowScopes {
  ConnectedFacility = 'connections.connectedfacility',
  ProviderReference = 'providers.providerreference',
  Need = 'staffing.need',
  Opportunity = 'assignments.opportunity',
  Clearance = 'connections.clearance',
  Assignment = 'assignments.assignment',
  AssignmentShift = 'assignments.assignmentshift',
  TimeCard = 'assignments.timecard',
}

/**
 * Workflow Event Codes
 * Equivalent to backend/workflows/constants.py EventCodes
 */
export enum EventCodes {
  SUCCESSFUL_LOGIN = 'SUCCESSFUL_LOGIN',
  FAILED_LOGIN = 'FAILED_LOGIN',
  LOGOUT = 'LOGOUT',
  FORGOT_PASSWORD = 'FORGOT_PASSWORD',
  UPDATE_PASSWORD = 'UPDATE_PASSWORD',
  ADMIN_RESET_PASSWORD = 'ADMIN_RESET_PASSWORD',
  FORCE_PASSWORD_CHANGE = 'FORCE_PASSWORD_CHANGE',
  ADDED_TO_AUTH_GROUP = 'ADDED_TO_AUTH_GROUP',
  REMOVED_FROM_AUTH_GROUP = 'REMOVED_FROM_AUTH_GROUP',
  PROV_ACCEPTED = 'PROV_ACCEPTED',
  FACILITY_APPROVED = 'FACILITY_APPROVED',
  TASK_COMPLETED = 'TASK_COMPLETED',
  CONF_LTR_SIGNED = 'CONF_LTR_SIGNED',
  REMOVED_HOLD = 'REMOVED_HOLD',
  REACTIVATED = 'REACTIVATED',
  PRIVILEGES_GRANTED = 'PRIVILEGES_GRANTED',
  PROV_INFO_RECVD = 'PROV_INFO_RECVD',
  FAC_APPRV_SHIFTS = 'FAC_APPRV_SHIFTS',
  TIMECARD_SIGNED = 'TIMECARD_SIGNED',
}

/**
 * Workflow Status Codes
 * Equivalent to backend/workflows/constants.py StatusCodes
 */
export enum StatusCodes {
  ACCEPTED = 'ACCEPTED',
  ACTIVE = 'ACTIVE',
  APPROVED = 'APPROVED',
  AWAIT_CLEAR = 'AWAIT_CLEAR',
  AWAIT_CONNECTION = 'AWAIT_CONNECTION',
  AWAIT_PRVLG = 'AWAIT_PRVLG',
  CANCELED = 'CANCELED',
  CLEARED = 'CLEARED',
  CLOSED = 'CLOSED',
  COMPLETED = 'COMPLETED',
  CONFIRMED = 'CONFIRMED',
  CONNECTED = 'CONNECTED',
  DECLINED = 'DECLINED',
  DISENGAGED = 'DISENGAGED',
  DORMANT = 'DORMANT',
  DRAFT = 'DRAFT',
  ENGAGING = 'ENGAGING',
  FINALIZED = 'FINALIZED',
  INFO_NEEDED = 'INFO_NEEDED',
  MAX_PROVIDERS = 'MAX_PROVIDERS',
  NEW = 'NEW',
  NOT_CLEARED = 'NOT_CLEARED',
  ON_HOLD = 'ON_HOLD',
  OPEN = 'OPEN',
  PAID = 'PAID',
  PAUSED = 'PAUSED',
  PAYABLE = 'PAYABLE',
  PEND_CLEAR = 'PEND_CLEAR',
  PEND_CONFIRM = 'PEND_CONFIRM',
  PEND_FAC_REV = 'PEND_FAC_REV',
  PEND_ONBOARD = 'PEND_ONBOARD',
  PEND_REGISTRATION = 'PEND_REGISTRATION',
  PENDING = 'PENDING',
  PROCESSED = 'PROCESSED',
  PROPOSED = 'PROPOSED',
  PROSPECTING = 'PROSPECTING',
  PROV_ACTION_REQ = 'PROV_ACTION_REQ',
  PROV_REV = 'PROV_REV',
  READY_TO_SHARE = 'READY_TO_SHARE',
  RECLEAR = 'RECLEAR',
  REOPENED = 'REOPENED',
  REQUESTED = 'REQUESTED',
  RESP_REQ = 'RESP_REQ',
  REVERIFIED = 'REVERIFIED',
  STARTED = 'STARTED',
  TERMINATED = 'TERMINATED',
  UNDELIVERABLE = 'UNDELIVERABLE',
  VERIFIED = 'VERIFIED',
  VOIDED = 'VOIDED',
  WITHDRAWN = 'WITHDRAWN',
  WORKING = 'WORKING',
}

/**
 * Workflow Status Category Codes
 * Equivalent to backend/workflows/constants.py StatusCategoryCodes
 */
export enum StatusCategoryCodes {
  DRAFT = 'DRAFT',
  NEW = 'NEW',
  INITIAL_CODE = NEW,
  RESUBMIT = 'RESUBMIT',
  IP = 'IP',
  CLOSED = 'CLOSED',
  LOCKED = 'LOCKED',
  ERROR = 'ERROR',
  HOLD = 'HOLD',
  CLEAR = 'CLEAR',
  FACREV = 'FACREV',
  ACTIVE = 'ACTIVE',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED',
  CXLD = 'CXLD',
  TERM = 'TERM',
  DENIED = 'DENIED',
  CMPLT = 'CMPLT',
}

/**
 * Workflow Status Category Kinds
 * Equivalent to backend/workflows/constants.py StatusCategoryKinds
 */
export enum StatusCategoryKinds {
  DRAFT = 'D',
  INITIAL = 'I',
  TRANSITORY = 'T',
  FINAL = 'F',
}

/**
 * Workflow Reason Codes
 * Equivalent to backend/workflows/constants.py ReasonCodes
 */
export enum ReasonCodes {
  FT = 'FT',
  PRN = 'PRN',
  ITT = 'ITT',
  SENDIT = 'SENDIT',
  LOCUM = 'LOCUM',
  FILLED = 'FILLED',
  CHANGE_SKILL_MIX = 'CHANGE_SKILL_MIX',
  CLOSE_SERVICE_LINE = 'CLOSE_SERVICE_LINE',
  CHANGE_VOLUME = 'CHANGE_VOLUME',
  CONTRACT_CLOSED = 'CONTRACT_CLOSED',
  OTHER = 'OTHER',
  PREVIOUS_RELATIONSHIP = 'PREVIOUS_RELATIONSHIP',
  ANOTHER_AGENCY = 'ANOTHER_AGENCY',
  BLOCK_STATE = 'BLOCK_STATE',
  COMMITED_ELSEWHERE = 'COMMITED_ELSEWHERE',
  NOT_INTERESTED = 'NOT_INTERESTED',
  BLOCK_ORGANIZATION = 'BLOCK_ORGANIZATION',
  BLOCK_FACILITY = 'BLOCK_FACILITY',
  TOO_FAR_AWAY = 'TOO_FAR_AWAY',
  NOT_ENOUGH_INFO = 'NOT_ENOUGH_INFO',
  FACILITY_REPUTATION = 'FACILITY_REPUTATION',
  RATES_TOO_LOW = 'RATES_TOO_LOW',
  WONT_CLEAR = 'WONT_CLEAR',
  UNMET_CRITERIA = 'UNMET_CRITERIA',
  LACKS_AVAILABILITY = 'LACKS_AVAILABILITY',
  PROV_UNRESPONSIVE = 'PROV_UNRESPONSIVE',
  PROV_NOT_NEEDED = 'PROV_NOT_NEEDED',
  SCHEDULING_CONFLICT = 'SCHEDULING_CONFLICT',
  INTERNAL_DECISION = 'INTERNAL_DECISION',
  MAX_PROVIDERS = 'MAX_PROVIDERS',
  OPP_CLOSED = 'OPP_CLOSED',
  FILLED_INTERNALLY = 'FILLED_INTERNALLY',
  FILLED_EXTERNALLY = 'FILLED_EXTERNALLY',
  COV_MDL_CHGD = 'COV_MDL_CHGD',
  BUDGET_OR_RATE = 'BUDGET_OR_RATE',
  DUP_OR_ERROR = 'DUP_OR_ERROR',
  ORG_UNRESPONSIVE = 'ORG_UNRESPONSIVE',
  ANOTHER_OPP = 'ANOTHER_OPP',
  RATE_ISSUE = 'RATE_ISSUE',
  TERMS_ISSUE = 'TERMS_ISSUE',
  PERSONAL = 'PERSONAL',
  FAC_NOT_FIT = 'FAC_NOT_FIT',
  UNMET_EXPECTATION = 'UNMET_EXPECTATION',
  ELIGIBILITY_ISSUE = 'ELIGIBILITY_ISSUE',
  PAYMENT_ISSUE = 'PAYMENT_ISSUE',
  PROV_NOT_FIT = 'PROV_NOT_FIT',
  PERFORM_QUALITY_ISSUE = 'PERFORM_QUALITY_ISSUE',
  PROV_UNRELIABLE = 'PROV_UNRELIABLE',
  NOT_STAFFING = 'NOT_STAFFING',
  PROV_WITHDREW = 'PROV_WITHDREW',
  PROV_INELIGIBLE = 'PROV_INELIGIBLE',
  LAPSED_PRIVILEGES = 'LAPSED_PRIVILEGES',
  AWAIT_APPROVAL = 'AWAIT_APPROVAL',
  AWAIT_COVG_PLAN = 'AWAIT_COVG_PLAN',
  AWAIT_FIN_REVIEW = 'AWAIT_FIN_REVIEW',
  TIMING_CONSIDER = 'TIMING_CONSIDER',
  CRED_DOCS = 'CRED_DOCS',
  LICENSURE_INFO = 'LICENSURE_INFO',
  INSURANCE_INFO = 'INSURANCE_INFO',
  WORK_HISTORY = 'WORK_HISTORY',
  AVAILABILITY_INFO = 'AVAILABILITY_INFO',
  RATE_INFO = 'RATE_INFO',
  TERMS_INFO = 'TERMS_INFO',
  DO_NOT_HIRE = 'DO_NOT_HIRE',
  NOT_CLEARED = 'NOT_CLEARED',
  RECENT_COMMS = 'RECENT_COMMS',
  CURRENT_RELATIONSHIP = 'CURRENT_RELATIONSHIP',
  SHIFT_ADDED = 'SHIFT_ADDED',
  SHIFT_REMOVED = 'SHIFT_REMOVED',
  SHIFT_CHANGED = 'SHIFT_CHANGED',
}

export enum QueueCodes {
  NEEDS_RESPONSE = 'needs_response',
  FOR_REVIEW = 'for_review',
  TO_CONFIRM = 'to_confirm',
  READY_FOR_CLEARANCE = 'ready_for_clearance',
  PROVIDER_REVIEW = 'provider_review',
  FACILITY_REVIEW = 'facility_review',
  UPCOMING = 'upcoming',
  COMPLETED = 'completed',
  CLOSED = 'closed',
  APPROVED = 'approved',
}
